import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Flag, ChartLineUp, Trophy, ArrowRight, Gift, Flame } from "@phosphor-icons/react";
import LiveCourseWidget from "@/components/LiveCourseWidget";
import CompProPanel from "@/components/CompProPanel";

export default function Dashboard() {
  const { user } = useAuth();
  const [rounds, setRounds] = useState([]);
  const [stats, setStats] = useState(null);
  const [founder, setFounder] = useState(null);
  // Surface the CompProPanel directly on the dashboard for admins only —
  // saves them a click into /admin every time they want to comp someone.
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    api.get("/rounds").then((r) => setRounds(r.data)).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/pro/founder-status").then((r) => setFounder(r.data)).catch(() => {});
    api.get("/admin/whoami").then((r) => setIsAdmin(!!r.data.is_admin)).catch(() => {});
  }, []);

  const idx = user?.handicap_index;
  const recent = rounds.slice(0, 4);
  const showFounder = founder?.available && !user?.is_pro;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between rise">
        <div>
          <span className="label-eyebrow">Bula, {user?.name?.split(" ")[0]}</span>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">Today's round.</h1>
        </div>
        <Link to="/rounds/new" data-testid="dashboard-new-round-btn" className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#E05D3A] text-white font-bold hover:brightness-110 transition">
          <Plus weight="bold" size={16} /> New round
        </Link>
      </div>

      <LiveCourseWidget />

      {showFounder && (
        <Link to="/pricing" data-testid="dashboard-founder-banner" data-tier={founder.tier}
          className="card-interactive p-4 sm:p-5 rise rise-delay-1 block overflow-hidden relative text-white"
          style={{ background: founder.tier === "founder"
            ? "linear-gradient(135deg, #E05D3A 0%, #C8421C 100%)"
            : "linear-gradient(135deg, #1E5631 0%, #133D21 100%)" }}>
          <div className="absolute inset-0 opacity-15 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 100% 0%, white 1px, transparent 1px)", backgroundSize: "20px 20px" }} />
          <div className="relative flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/15 grid place-items-center shrink-0">
                <Flame weight="fill" size={20} />
              </div>
              <div>
                <div className="font-heading font-black text-base sm:text-lg leading-tight">
                  {founder.tier === "founder"
                    ? "Launch offer · 50% off Year 1 of Bula Pro"
                    : `Early-adopter offer · ${founder.discount_pct}% off Year 1 of Bula Pro`}
                </div>
                <div className="text-xs sm:text-sm opacity-90">
                  <span className="font-bold tabular-nums" data-testid="dashboard-founder-remaining">{founder.remaining}/{founder.total_spots}</span> {founder.tier === "founder" ? "founder" : "early-adopter"} spots left · FJ${founder.founder_amount} for the first year
                </div>
              </div>
            </div>
            <span className="inline-flex items-center gap-1 text-sm font-bold">Claim it <ArrowRight weight="bold" size={14} /></span>
          </div>
        </Link>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6">
        <div className="md:col-span-2 md:row-span-2 card-default p-8 relative overflow-hidden rise rise-delay-1" data-testid="handicap-card">
          <div className="absolute -right-12 -top-12 w-64 h-64 rounded-full bg-[#E05D3A]/10" />
          <span className="label-eyebrow">WHS Handicap Index</span>
          <div className="font-heading font-black text-[6rem] leading-none mt-2 tracking-tighter" data-testid="whs-index-value">
            {idx == null ? "—" : idx.toFixed(1)}
          </div>
          <p className="text-[#0F2537]/70 mt-3 max-w-xs">
            {idx == null
              ? `Record at least 3 rounds to establish your handicap index.`
              : `Calculated from your best ${Math.min(rounds.length, 20)} of last ${Math.min(rounds.length, 20)} rounds.`}
          </p>
          <Link to="/stats" data-testid="dashboard-view-progression" className="mt-6 inline-flex items-center gap-1 text-sm font-bold text-[#0F2537] hover:text-[#E05D3A]">
            View progression <ArrowRight size={14} weight="bold" />
          </Link>
        </div>

        <StatTile label="Rounds played" value={stats?.rounds ?? 0} testid="tile-rounds" />
        <StatTile label="Avg score" value={stats?.avg_score ?? "—"} testid="tile-avg" />
        <StatTile label="Best score" value={stats?.best_score ?? "—"} testid="tile-best" />
        <StatTile label="Avg differential" value={stats?.avg_diff ?? "—"} testid="tile-avg-diff" />
      </div>

      <Link to="/referrals" className="card-interactive p-5 rise rise-delay-2 text-white block" style={{ background: "linear-gradient(135deg, #0F2537 0%, #1a3a52 100%)" }} data-testid="dashboard-refer-banner">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Gift size={22} weight="fill" className="text-[#E05D3A]" />
            <div>
              <div className="font-heading font-bold text-lg leading-tight">Refer a mate · both get 1 month of Pro free</div>
              <div className="text-xs text-white/70 mt-0.5">Tap to grab your code →</div>
            </div>
          </div>
          <span className="text-xs font-bold uppercase tracking-widest bg-[#E05D3A] text-white px-2 py-1 rounded-full">Earn Pro</span>
        </div>
      </Link>

      <div className="grid md:grid-cols-3 gap-4 md:gap-6">
        <Link to="/courses" className="card-interactive p-6 rise rise-delay-2" data-testid="quick-courses">
          <Flag size={24} weight="duotone" className="text-[#E05D3A]" />
          <h3 className="font-heading font-bold text-lg mt-3">Browse Fiji courses</h3>
          <p className="text-sm text-[#0F2537]/70 mt-1">5 pre-loaded courses ready to play.</p>
        </Link>
        <Link to={user?.is_pro ? "/pro/club-averages" : "/pricing"} className="card-interactive p-6 rise rise-delay-3 relative" data-testid="quick-club-avg">
          {!user?.is_pro && (
            <span className="absolute top-3 right-3 text-[10px] font-bold tracking-widest text-[#E05D3A]">PRO</span>
          )}
          <ChartLineUp size={24} weight="duotone" className="text-[#E05D3A]" />
          <h3 className="font-heading font-bold text-lg mt-3">Club averages</h3>
          <p className="text-sm text-[#0F2537]/70 mt-1">{user?.is_pro ? "By par 3/4/5 hole." : "Unlock with Bula Pro."}</p>
        </Link>
        <Link to="/leaderboard" className="card-interactive p-6 rise rise-delay-4" data-testid="quick-leaderboard">
          <Trophy size={24} weight="duotone" className="text-[#E05D3A]" />
          <h3 className="font-heading font-bold text-lg mt-3">Leaderboard</h3>
          <p className="text-sm text-[#0F2537]/70 mt-1">Where do you stack up?</p>
        </Link>
      </div>

      <div className="card-default p-6 rise">
        <div className="flex items-end justify-between mb-4">
          <div>
            <span className="label-eyebrow">Recent rounds</span>
            <h2 className="font-heading font-bold text-2xl mt-0.5">Last cards</h2>
          </div>
          <Link to="/rounds" data-testid="view-all-rounds" className="text-sm font-bold text-[#0F2537] hover:text-[#E05D3A]">View all →</Link>
        </div>
        {recent.length === 0 ? (
          <div className="py-10 text-center text-[#0F2537]/60">
            <p>No rounds yet.</p>
            <Link to="/rounds/new" data-testid="empty-new-round-link" className="mt-3 inline-block text-[#E05D3A] font-bold">Record your first round →</Link>
          </div>
        ) : (
          <ul className="divide-y divide-[#E2E0D8]">
            {recent.map((r) => (
              <li key={r.id}>
                <Link to={`/rounds/${r.id}`} className="flex items-center justify-between py-3 hover:bg-black/[0.02] -mx-2 px-2 rounded-lg" data-testid={`recent-round-${r.id}`}>
                  <div>
                    <div className="font-semibold">{r.course_name}</div>
                    <div className="text-xs text-[#0F2537]/60">{new Date(r.played_at).toLocaleDateString()} · {r.tees} tees</div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading font-black text-2xl">{r.total_strokes}</div>
                    <div className="text-xs text-[#0F2537]/60">{r.differential > 0 ? "+" : ""}{r.differential} diff</div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Admin-only quick action right on the home page. The endpoint itself
       * enforces admin auth — this just hides the UI for everyone else. */}
      {isAdmin && (
        <div data-testid="dashboard-admin-section">
          <CompProPanel />
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, testid }) {
  return (
    <div className="card-default p-6 rise rise-delay-2" data-testid={testid}>
      <span className="label-eyebrow">{label}</span>
      <div className="font-heading font-black text-3xl mt-2">{value ?? "—"}</div>
    </div>
  );
}
